import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useUnpaidOrders } from "../../modules/user/stores";
import { useStartupStore } from "../../modules/startup/stores";
import { useUserDataFlattened } from "../../modules/user/stores";
import { Dialog, Button } from "../Base";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { useT } from "../../i18n/hooks";
import { useCurrencySymbol } from "../../hooks/useUtils";
import utils from "../../utils/utils";
import "./UnpaidGuideController.scss";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatCreatedDate(created?: number | string): string {
  if (!created) return "";
  const d = typeof created === "number" ? new Date(created) : new Date(created);
  if (isNaN(d.getTime())) return String(created);
  const mon = MONTHS[d.getMonth()];
  const day = d.getDate();
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${mon} ${day}, ${year} ${hh}:${mm}`;
}

const BLOCKED_PATHS = ["/unpaid-orders", "/game/"];

const GUIDE_SESSION_KEY = "unpaid_guide_shown_ids";

function getShownIds(): string[] {
  const v = utils.getObject(GUIDE_SESSION_KEY);
  return Array.isArray(v) ? v : [];
}

function markShown(orderId: string) {
  const ids = getShownIds();
  if (!ids.includes(orderId)) {
    ids.push(orderId);
    utils.setObject(GUIDE_SESSION_KEY, ids);
  }
}

function isBlockedPath(pathname: string): boolean {
  return BLOCKED_PATHS.some(p => pathname.startsWith(p));
}

export const UnpaidGuideController: React.FC = () => {
  const t = useT();
  const { navigate } = useNavigation();
  const { isAuthenticated } = useUserDataFlattened();
  const enableLastPaymentVoucher = useStartupStore(
    state => !!state.startupData?.paymentVoucherTemplate?.enabled
  );
  const { unpaidOrders, fetchUnpaidOrders } = useUnpaidOrders();
  const location = useLocation();

  const [visible, setVisible] = useState(false);
  const [targetOrderId, setTargetOrderId] = useState<string | null>(null);
  const prevOrderIdsRef = useRef<string[]>([]);
  const hasCheckedNewRef = useRef(false);

  const shouldRun = isAuthenticated && enableLastPaymentVoucher;

  useEffect(() => {
    if (visible && isBlockedPath(location.pathname)) {
      setVisible(false);
    }
  }, [location.pathname, visible]);

  const findPendingOrder = useCallback(() => {
    const shown = getShownIds();
    return (
      unpaidOrders.find(o => {
        const n = (o.status || "").toUpperCase();
        if (n !== "PENDING") return false;
        if (o.subCurrencyFlag) return false;
        const hasVoucher =
          (Array.isArray(o.paymentVoucherImgs) &&
            o.paymentVoucherImgs.length > 0) ||
          (Array.isArray(o.numberIds) && o.numberIds.length > 0);
        if (hasVoucher) return false;
        if (shown.includes(o.id)) return false;
        return true;
      }) || null
    );
  }, [unpaidOrders]);

  useEffect(() => {
    if (!shouldRun) return;

    const currentIds = unpaidOrders.map(o => o.id);
    const prevIds = prevOrderIdsRef.current;
    const newIds = currentIds.filter(id => !prevIds.includes(id));
    prevOrderIdsRef.current = currentIds;

    if (
      newIds.length > 0 &&
      hasCheckedNewRef.current &&
      !isBlockedPath(location.pathname)
    ) {
      const order = findPendingOrder();
      if (order && newIds.includes(order.id)) {
        setTargetOrderId(order.id);
        setVisible(true);
      }
    }
    hasCheckedNewRef.current = true;
  }, [unpaidOrders, shouldRun, findPendingOrder, location.pathname]);

  useEffect(() => {
    if (!shouldRun) return;

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        !isBlockedPath(location.pathname)
      ) {
        fetchUnpaidOrders().then(() => {
          const order = findPendingOrder();
          if (order) {
            setTargetOrderId(order.id);
            setVisible(true);
          }
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [shouldRun, fetchUnpaidOrders, findPendingOrder, location.pathname]);

  const handleClose = useCallback(() => {
    setVisible(false);
    if (targetOrderId) markShown(targetOrderId);
  }, [targetOrderId]);

  const handleHavePaid = useCallback(() => {
    setVisible(false);
    if (targetOrderId) markShown(targetOrderId);
    navigate(ROUTES.UNPAID_ORDERS);
  }, [targetOrderId, navigate]);

  const targetOrder = unpaidOrders.find(o => o.id === targetOrderId) || null;
  const currencySymbol = useCurrencySymbol();

  if (!shouldRun) return null;

  return (
    <Dialog
      visible={visible}
      variant="primary"
      size="md"
      closeOnAction={false}
      className="unpaid-guide-dialog"
      content={
        <div className="unpaid-guide-dialog__body">
          <div className="unpaid-guide-dialog__title">
            {t("unpaidOrders.guideTitle")}
          </div>
          {targetOrder && (
            <div className="unpaid-guide-dialog__card">
              <div className="unpaid-guide-dialog__card-header">
                <div className="unpaid-guide-dialog__card-info">
                  <div className="unpaid-guide-dialog__order-no">
                    {t("unpaidOrders.orderNo")} {targetOrder.id}
                  </div>
                  <div className="unpaid-guide-dialog__amount">
                    {targetOrder.currencySymbol || currencySymbol}
                    {targetOrder.amount}
                  </div>
                </div>
              </div>
              <div className="unpaid-guide-dialog__created">
                {t("unpaidOrders.created")}:{" "}
                {formatCreatedDate(targetOrder.created)}
              </div>
            </div>
          )}
        </div>
      }
      customActions={
        <div className="unpaid-guide-dialog__actions">
          <Button
            className="unpaid-guide-dialog__btn"
            variant="secondary"
            onClick={handleClose}
          >
            {t("payment.cancel")}
          </Button>
          <Button
            className="unpaid-guide-dialog__btn"
            variant="primary"
            onClick={handleHavePaid}
          >
            {t("payment.havePaid")}
          </Button>
        </div>
      }
    />
  );
};

export default UnpaidGuideController;
